
import { Users, X, BadgeCheck } from 'lucide-react';
import { Badge } from '../../../shared/components/ui/Badge';

interface User {
    id: number;
    name: string;
    email: string;
    ltv: number;
    orders: number;
    lastActive: string;
    status: string;
    intent: string;
    tags: string[];
}

interface UserProfileModalProps {
    user: User;
    onClose: () => void;
}

const UserProfileModal = ({ user, onClose }: UserProfileModalProps) => (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-md animate-fade-in">
        <div role="dialog" aria-modal="true" aria-labelledby="user-profile-title" className="bg-white w-full max-w-md h-full shadow-2xl relative flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 bg-white flex justify-between items-center flex-shrink-0">
                <h2 id="user-profile-title" className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Users size={20} className="text-violet-500" /> Perfil de Usuario
                </h2>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-900 transition-colors"
                >
                    <X size={20} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {/* 1. Order Status Banner (Top Priority) */}
                <div className={`p-6 ${user.status === 'Active Cart' ? 'bg-green-50 border-b border-green-100' : 'bg-violet-50 border-b border-violet-100'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-bold uppercase tracking-wider ${user.status === 'Active Cart' ? 'text-green-700' : 'text-violet-700'}`}>
                            Estado del Pedido
                        </span>
                        <div className={`w-2 h-2 rounded-full animate-pulse ${user.status === 'Active Cart' ? 'bg-green-500' : 'bg-violet-500'}`}></div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${user.status === 'Active Cart' ? 'bg-green-100 text-green-700' : 'bg-violet-100 text-violet-700'}`}>
                            {user.status === 'Active Cart' ? <Users size={24} /> : <BadgeCheck size={24} />}
                        </div>
                        <div>
                            <div className={`text-lg font-bold ${user.status === 'Active Cart' ? 'text-green-900' : 'text-violet-900'}`}>{user.status}</div>
                            <div className={`text-xs ${user.status === 'Active Cart' ? 'text-green-600' : 'text-violet-600'}`}>Actualizado en tiempo real</div>
                        </div>
                    </div>
                </div>

                {/* 2. Synchronized Data Section */}
                <div className="p-6 space-y-8">
                    {/* User Header */}
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-slate-900 text-white flex items-center justify-center text-2xl font-bold shadow-lg shadow-slate-200">
                            {user.name.charAt(0)}
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-slate-900">{user.name}</div>
                            <div className="text-slate-500 flex items-center gap-2 text-sm">
                                {user.email}
                                <Badge color="violet" className="text-[10px] py-0.5">Verificado</Badge>
                            </div>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                            Datos Sincronizados
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <div className="text-xs text-slate-500 uppercase font-bold mb-2 tracking-wider">LTV</div>
                                <div className="text-2xl font-bold text-slate-900 font-mono">${user.ltv.toLocaleString('es-AR')}</div>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <div className="text-xs text-slate-500 uppercase font-bold mb-2 tracking-wider">Pedidos</div>
                                <div className="text-2xl font-bold text-slate-900">{user.orders}</div>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 col-span-2">
                                <div className="text-xs text-slate-500 uppercase font-bold mb-2 tracking-wider">Intención de Compra</div>
                                <div className="flex items-center justify-between">
                                    <div className={`text-lg font-bold ${user.intent === 'High' ? 'text-green-600' : 'text-slate-900'}`}>{user.intent === 'High' ? 'Alta' : user.intent === 'Medium' ? 'Media' : 'Baja'}</div>
                                    <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div className={`h-full ${user.intent === 'High' ? 'bg-green-500 w-3/4' : 'bg-slate-400 w-1/3'}`}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="bg-slate-50 p-5 rounded-xl border border-slate-100 border-dashed">
                        <div className="text-xs text-slate-500 uppercase font-bold mb-3 tracking-wider flex items-center gap-2">
                            Etiquetas IA
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {user.tags.map(tag => (
                                <span key={tag} className="px-2.5 py-1 bg-white text-slate-600 rounded-md text-xs font-semibold border border-slate-200 shadow-sm">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export default UserProfileModal;
